import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { useReduxLoading } from '@core/hooks';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import cancelarAlert from 'app/services/alerts/cancelarAlert';
import { FormikProvider, useFormik } from 'formik';
import React, { useContext, useEffect } from 'react';
import { FaCreditCard } from 'react-icons/fa';
import * as Yup from 'yup';
import { ContaContext } from '../../../ContaContext';
import { modaisConta } from '../modais';
import { bandeiras } from './bandeiras';
import EditarContaCartaoForm from './EditarContaCartaoForm';

const getDigitosCartao = (digitos) => {
  if (typeof digitos === 'number' && digitos !== 0) {
    return `${digitos}`.padStart(4, '0');
  }
  return digitos;
};

const EditarContaCartaoModal = ({ onSubmit }) => {
  const [modal, setModal] = useContext(ContaContext).modal;
  const [conta] = useContext(ContaContext).contaAtual;
  const [loading, setLoading] = useContext(ContaContext).loading;
  const [page] = useContext(ContaContext).page;
  const [size] = useContext(ContaContext).size;

  useReduxLoading(loading);

  const contaCartaoSchema = Yup.object().shape({
    descricao: Yup.string()
      .nullable()
      .required(mensagemCampoObrigatorio('a descricao'))
      .max(30, 'A descrição não pode ultrapassar 30 caracteres'),
    contaCartao: Yup.object().shape({
      digitosCartao: Yup.string()
        .nullable()
        .required(mensagemCampoObrigatorio('os últimos 4 dígitos do cartão'))
        .min(4, 'É necessário 4 digitos')
        .max(4),
      emissorId: Yup.string()
        .nullable()
        .required(mensagemCampoObrigatorio('o emissor do cartão')),
      contaPadraoPagamento: Yup.object().shape({
        id: Yup.string().nullable(),
        // .required('É necessário informar uma conta'),
      }),
      diaFechamento: Yup.number()
        .nullable()
        .required(mensagemCampoObrigatorio('o dia de fechamento'))
        .min(1, 'Dia inválido')
        .max(31, 'Dia inválido'),
      diaVencimento: Yup.number()
        .nullable()
        .required(mensagemCampoObrigatorio('o dia de vencimento'))
        .min(1, 'Dia inválido')
        .max(31, 'Dia inválido'),
      bandeira: Yup.string().nullable(),
    }),
  });

  const handleSubmit = async (data) => {
    if (!formik.dirty) {
      handleClose();
    } else {
      setLoading(true);
      const request = JSON.parse(JSON.stringify(data));
      request.id = conta?.id;
      request.contaCartao.id = conta?.contaCartao?.id;
      request.contaCartao.contaPadraoPagamentoId = data?.contaCartao?.contaPadraoPagamentoId?.id;

      const res = await onSubmit(request, { page, size });

      setLoading(false);
      if (res) {
        setModal('');
      }
    }
  };

  const initialValues = {
    descricao: conta?.descricao || '',
    contaCartao: {
      digitosCartao: getDigitosCartao(conta?.contaCartao?.digitosCartao) || '',
      emissorId: conta?.contaCartao?.emissorId || '',
      contaPadraoPagamentoId: conta?.contaCartao?.contaPadraoPagamentoId || '',
      diaFechamento: conta?.contaCartao?.diaFechamento || '',
      diaVencimento: conta?.contaCartao?.diaVencimento || '',
      bandeira: conta?.contaCartao?.bandeira || bandeiras.outro,
    },
  };

  const initialErrors = {
    principal: false,
  };

  const formik = useFormik({
    onSubmit: handleSubmit,
    validationSchema: contaCartaoSchema,
    initialValues,
    initialErrors,
    validateOnBlur: true,
    validateOnChange: true,
  });

  const handleClose = () => {
    const close = () => {
      formik.resetForm(initialValues);
      setModal('');
    };

    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  useEffect(() => {
    formik.resetForm(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conta]);

  const Buttons = (
    <>
      <CancelButton title="cancelar" type="button" onClick={handleClose}>
        Cancelar
      </CancelButton>
      <SaveButton color="buttonSave" type="button" onClick={formik.handleSubmit}>
        Salvar
      </SaveButton>
    </>
  );

  return (
    <FormikProvider value={formik}>
      <SimpleDialog fullScreen open={modal === modaisConta.editarContaCartao} onClose={handleClose}>
        <SimpleDialogContent
          fullScreen
          header={{ title: 'Cadastro de cartão de crédito', icon: <FaCreditCard />, toolbar: Buttons }}
          content={<EditarContaCartaoForm />}
        />
      </SimpleDialog>
    </FormikProvider>
  );
};

export default EditarContaCartaoModal;
